import { situacaoEm, type IntervencaoPartida } from './partida';
import { LISTA_FORMACOES, slotsDaFormacao } from './tatica';
import { LISTA_ESTILOS, LISTA_PASSES, LISTA_PRESSOES } from './taticas';
import type { Clube, Formacao } from './tipos';
import type { PartidaJogada } from './jogo';

/** O que o técnico pediu com a partida pausada (online e solo): substituições, tática e, se quiser, uma formação nova. */
export interface PedidoIntervencao {
  subs?: { saiId: number; entraId: number }[];
  estilo?: string;
  marcacao?: string;
  ataque?: string;
  passe?: string;
  pressao?: string;
  formacao?: { nome: string; ordem: (number | null)[] };
}

function inteiro(v: unknown, nome: string): number {
  if (typeof v !== 'number' || !Number.isInteger(v)) throw new Error(`${nome} inválido`);
  return v;
}

/** Confere substituições e táticas de uma intervenção feita depois do minuto `minuto` (as trocas valem no minuto seguinte). Lança Error se algo não vale. */
export function construirIntervencao(clube: Clube, partida: PartidaJogada, minuto: number, cmd: PedidoIntervencao): IntervencaoPartida {
  const lado = partida.mandante === clube.id ? 0 : 1;
  const sit = situacaoEm(partida.resultado, lado, minuto);
  const emCampo = new Set(sit.ocupantes.filter((x): x is number => x !== null));
  const noBanco = new Set(sit.banco);

  const subs = cmd.subs ?? [];
  if (!Array.isArray(subs) || subs.length > 5) throw new Error('substituições inválidas');
  if (sit.substituicoes + subs.length > 5) throw new Error(`só restam ${5 - sit.substituicoes} substituições`);
  const saem = new Set<number>();
  const entram = new Set<number>();
  for (const s of subs) {
    const saiId = inteiro(s?.saiId, 'jogador que sai');
    const entraId = inteiro(s?.entraId, 'jogador que entra');
    if (!emCampo.has(saiId)) throw new Error('quem sai precisa estar em campo');
    if (!noBanco.has(entraId)) throw new Error('quem entra precisa estar no banco');
    if (saem.has(saiId) || entram.has(entraId)) throw new Error('jogador repetido nas substituições');
    const sai = clube.elenco.find((x) => x.id === saiId);
    const entra = clube.elenco.find((x) => x.id === entraId);
    if (!sai || !entra) throw new Error('jogador inválido');
    const slot = sit.slots[sit.ocupantes.indexOf(saiId)];
    if ((slot === 'G') !== (entra.posicao === 'G')) throw new Error('goleiro só é trocado por outro goleiro');
    saem.add(saiId);
    entram.add(entraId);
  }
  const iv: IntervencaoPartida = { minuto: minuto + 1, subs: subs.map((s) => ({ saiId: s.saiId, entraId: s.entraId })) };
  const enumDe = <T extends string>(v: unknown, validos: readonly T[], nome: string): T | undefined => {
    if (v === undefined || v === null || v === '') return undefined;
    if (!validos.includes(v as T)) throw new Error(`${nome} inválido`);
    return v as T;
  };
  const estilo = enumDe(cmd.estilo, LISTA_ESTILOS, 'estilo');
  const marcacao = enumDe(cmd.marcacao, ['leve', 'normal', 'forte'] as const, 'marcação');
  const ataque = enumDe(cmd.ataque, ['meio', 'lados', 'equilibrado'] as const, 'concentração de ataque');
  const passe = enumDe(cmd.passe, LISTA_PASSES, 'estilo de passe');
  const pressao = enumDe(cmd.pressao, LISTA_PRESSOES, 'pressão');
  if (estilo) iv.estilo = estilo;
  if (marcacao) iv.marcacao = marcacao;
  if (ataque) iv.ataque = ataque;
  if (passe) iv.passe = passe;
  if (pressao) iv.pressao = pressao;
  if (cmd.formacao !== undefined && cmd.formacao !== null) {
    const nome = cmd.formacao.nome as Formacao;
    if (!LISTA_FORMACOES.includes(nome)) throw new Error('formação inválida');
    const slotsNovos = slotsDaFormacao(nome);
    const ordem = cmd.formacao.ordem;
    if (!Array.isArray(ordem) || ordem.length !== slotsNovos.length) throw new Error('escalação da formação inválida');
    // quem estará em campo depois das substituições, e quem é o goleiro entre eles
    const sai = new Map(subs.map((s) => [s.saiId, s.entraId]));
    const depois = sit.ocupantes.map((id) => (id === null ? null : (sai.get(id) ?? id)));
    const finais = new Set(depois.filter((x): x is number => x !== null));
    const ids = ordem.filter((x): x is number => x !== null);
    if (ordem.some((x) => x !== null && !Number.isInteger(x))) throw new Error('escalação da formação inválida');
    if (new Set(ids).size !== ids.length || ids.length !== finais.size || ids.some((id) => !finais.has(id))) throw new Error('a escalação deve ter todos os jogadores que estão em campo, uma vez cada');
    const iGol = sit.slots.indexOf('G');
    const goleiro = iGol >= 0 ? (depois[iGol] ?? null) : null;
    if (ordem[slotsNovos.indexOf('G')] !== goleiro) throw new Error('o goleiro precisa continuar no gol');
    iv.formacao = { nome, ordem: [...ordem] as (number | null)[] };
  }
  return iv;
}

/** A intervenção muda alguma coisa (troca, tática ou formação)? */
export function intervencaoMuda(iv: IntervencaoPartida): boolean {
  return iv.subs.length > 0 || Boolean(iv.estilo || iv.marcacao || iv.ataque || iv.passe || iv.pressao || iv.formacao);
}
